import React, { useState } from "react";

const LinkedAccountPasswordModal = ({
  email,
  roles = [],
  targetRole,
  onVerify,
  onForgotPassword,
  onUseDifferentEmail,
}) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await onVerify(password);
    } catch (verificationError) {
      setError(
        verificationError?.message || "Unable to verify your password.",
      );
    } finally {
      setLoading(false);
    }
  };

  const roleLabels = {
    member: "Member",
    merchant: "Merchant",
    fundraiser: "Fundraiser",
  };
  const targetRoleLabel = roleLabels[targetRole] || "account";

  return (
    <div className="fixed inset-0 z-[9999] flex min-h-screen items-center justify-center bg-black/50 px-4 max-sm:items-end max-sm:px-0">
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-[440px] rounded-[20px] bg-white px-6 py-6 shadow-[0_12px_36px_rgba(0,0,0,0.18)] max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-[24px] max-sm:px-6 max-sm:pb-8"
        role="dialog"
        aria-modal="true"
        aria-labelledby="linked-account-title"
      >
        <button
          type="button"
          onClick={onUseDifferentEmail}
          aria-label="Close and use a different email"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center text-[24px] leading-none text-[#8C8C8C]"
        >
          &times;
        </button>
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F3FF] text-[#00ADEF]">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10 13.5a4.5 4.5 0 006.36 0l3-3A4.5 4.5 0 0013 4.14l-1.72 1.72M14 10.5a4.5 4.5 0 00-6.36 0l-3 3A4.5 4.5 0 0011 19.86l1.72-1.72"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2
          id="linked-account-title"
          className="font-poppins text-[18px] font-semibold text-[#303030]"
        >
          You already have a Swoodle account
        </h2>
        <p className="mt-2 break-all font-poppins text-[12px] leading-5 text-[#767676]">
          {email} is already registered as:
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {roles.map((role) => (
            <span
              key={role}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F3FF] px-3 py-1 font-poppins text-[12px] font-medium text-[#008FD0]"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <circle cx="8" cy="5" r="3" />
                <path d="M2.5 14c.2-3 2.4-4.5 5.5-4.5s5.3 1.5 5.5 4.5H2.5z" />
              </svg>
              {roleLabels[role] || role}
            </span>
          ))}
        </div>
        <p className="mt-3 font-poppins text-[12px] leading-[18px] text-[#767676]">
          Enter your existing Swoodle password to add a {targetRoleLabel} account.
          You’ll use the same email and password for both.
        </p>

        <label
          className="mt-3 block font-poppins text-[12px] font-medium text-[#303030]"
          htmlFor="linked-account-password"
        >
          Swoodle password
        </label>
        <div className="mt-1 flex h-[40px] items-center rounded-[9px] border border-[#E1E5EA] px-3">
          <input
            id="linked-account-password"
            autoFocus
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            className="h-full min-w-0 flex-1 border-0 bg-transparent font-poppins text-[13px] outline-none"
            aria-invalid={Boolean(error)}
          />
          <button
            type="button"
            onClick={() => setShowPassword((visible) => !visible)}
            className="ml-2 flex h-7 w-7 items-center justify-center text-[#8C9AA8]"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6S2 12 2 12z" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M3 3l18 18M10.6 6.2A10.7 10.7 0 0112 6c6.4 0 10 6 10 6s-1.4 2.4-4.1 4.2M6.2 6.8C3.5 8.6 2 12 2 12s3.6 6 10 6c1.4 0 2.6-.3 3.7-.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>
        {error && (
          <p className="mt-2 font-poppins text-[13px] text-red-600" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={onForgotPassword}
          className="mt-3 font-poppins text-[12px] font-medium text-[#00ADEF]"
        >
          Forgot password?
        </button>
        <div className="mt-4 flex gap-2 max-sm:flex-col">
          <button
            type="button"
            onClick={onUseDifferentEmail}
            className="h-[40px] flex-1 rounded-[9px] border border-[#E1E5EA] font-poppins text-[12px] font-medium text-[#303030] hover:bg-[#F8FAFC]"
          >
            Use a different email
          </button>
          <button
            type="submit"
            disabled={!password || loading}
            className="h-[40px] flex-1 rounded-[9px] bg-[#00ADEF] font-poppins text-[12px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Verifying..." : `Add ${targetRoleLabel} account`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LinkedAccountPasswordModal;
